import { BaseModel } from '@red/data-access';
import { Expose, Transform } from 'class-transformer';
import { IAuditTrail, UserInterface, IAuditTrailAlignField } from '../interfaces/audit-trail.interface';
import { AUDIT_MODULE_DATA, AuditActions } from '../data/audit-trail.data';
import { AuditModule, RecordTypesConfig, RecordTypesECDD, RecordTypesFinance, RecordTypesHrm, RecordTypesTransaction } from '../enums/audit-trail.enum';
import { blankFormAdjFieldsHide, haTransactionFieldsHide } from '../data/audit-trail-transaction.data';

export class AuditTrailModel extends BaseModel implements IAuditTrail {
  @Expose()
  id!: number;

  @Expose()
  uuid!: string;

  @Expose()
  endpoint!: string;

  @Expose()
  method!: string;

  @Expose()
  ip!: string;

  @Expose()
  username!: string;

  @Expose()
  moduleName!: AuditModule;

  @Expose()
  entityName!: RecordTypesConfig | RecordTypesTransaction | RecordTypesFinance | RecordTypesHrm | RecordTypesECDD;

  @Expose()
  entityId!: string;

  @Expose()
  action!: string;

  @Expose()
  createdAt!: Date;

  @Expose()
  createdBy!: number;

  @Expose()
  changes: any;

  @Expose()
  user!: UserInterface;

  // @Expose()
  // updatedBy!: UpdatedByModel;

  @Expose()
  oldValues: any;

  @Expose()
  newValues: any;

  @Expose()
  @Transform(({ obj, value }) => {
    if (obj?.moduleName && obj?.entityName) {
      const arrHide: string[] = AUDIT_MODULE_DATA[obj.moduleName as AuditModule]?.['recordTypes'][obj.entityName]?.fieldsHide ?? [];
      return arrHide;
    }
    return [];
  })
  fieldsHide!: string[];

  @Expose()
  @Transform(({ obj, value }) => {
    if (obj?.moduleName && obj?.entityName) {
      const fields: string[] = AUDIT_MODULE_DATA[obj.moduleName as AuditModule]?.['recordTypes'][obj.entityName]?.fieldsFilterActive ?? [];
      return fields;
    }
    return [];
  })
  fieldsFilterActive!: string[];

  @Expose()
  @Transform(({ obj }) => {
    if (obj?.moduleName && obj?.entityName) {
      const fields: string[] = AUDIT_MODULE_DATA[obj.moduleName as AuditModule]?.['recordTypes'][obj.entityName]?.fieldsImage ?? [];
      return fields;
    }
    return [];
  })
  fieldsImage!: string[];

  @Expose()
  @Transform(({ obj }) => {
    if (obj?.moduleName && obj?.entityName) {
      const fields: string[] = AUDIT_MODULE_DATA[obj.moduleName as AuditModule]?.['recordTypes'][obj.entityName]?.fieldsObjectToArray ?? [];
      return fields;
    }
    return [];
  })
  fieldsObjectToArray!: string[];

  @Expose()
  @Transform(({ obj }) => {
    if (obj?.moduleName && obj?.entityName) {
      const fields: string[] = AUDIT_MODULE_DATA[obj.moduleName as AuditModule]?.['recordTypes'][obj.entityName]?.fieldsHideIndex ?? [];
      return fields;
    }
    return [];
  })
  fieldsHideIndex!: string[];

  get displayRecordType() {
    if (this.moduleName && this.entityName) {
      return AUDIT_MODULE_DATA[this.moduleName]?.['recordTypes'][this.entityName]?.viewValue;
    }
    return ''
  };

  get fieldNameMap(): { [key: string]: string } | null {
    if (this.moduleName && this.entityName) {
      return AUDIT_MODULE_DATA[this.moduleName]?.['recordTypes'][this.entityName]?.fieldNameMap;
    }
    return null;
  };

  get fieldsAlign(): IAuditTrailAlignField[] {
    if (this.moduleName && this.entityName) {
      return AUDIT_MODULE_DATA[this.moduleName]?.['recordTypes'][this.entityName]?.fieldsAlign ?? [];
    }
    return [];
  };


  get getSpecialFieldsHide(): string[] {
    let arrHide: string[] = [];
    const allKeys = new Set([...Object.keys(this.oldValues || {}), ...Object.keys(this.newValues || {})]);
    // need hide some special fields
    allKeys.forEach(key => {
      const valueOld = this.oldValues?.[key];
      const valueNew = this.newValues?.[key];
      if (this.entityName === RecordTypesTransaction.TransactionEntity || this.entityName === RecordTypesTransaction.BlankFormEntity) {
        if (haTransactionFieldsHide.includes(key)) {
          if (key?.toLocaleLowerCase().includes('price') && typeof valueOld !== 'number' && typeof valueNew !== 'number') {
            arrHide.push(key);
          } else if ((valueOld === null || valueOld === undefined) && (valueNew === null || valueNew === undefined)) {
            arrHide.push(key);
          }
        }
        if (['transactionCode', 'blankFormCode'].includes(key) && (!this.oldValues || valueOld) && valueNew) {
          arrHide = arrHide.concat(...blankFormAdjFieldsHide);
        }
      } else if (this.entityName === RecordTypesTransaction.BatchEntity || this.entityName === RecordTypesTransaction.BatchBlankFormEntity) {
        switch (key) {
          case 'devGrossComm':
            if ((valueOld?.totalAmount === 0 || valueOld === null || valueOld === undefined) && (valueNew?.totalAmount === 0 || valueNew === null || valueNew === undefined)) {
              arrHide = arrHide.concat(...[key, `${key}.subTotalAmount`, `${key}.taxAmount`, `${key}.totalAmount`]);
            } else {
              arrHide = arrHide.concat(...['grossComm.subTotalAmount', 'grossComm.taxAmount', 'grossComm.totalAmount']);
            }
            break;
          case 'profomaGrossComm':
            if ((valueOld?.totalAmount === 0 || valueOld === null || valueOld === undefined) && (valueNew?.totalAmount === 0 || valueNew === null || valueNew === undefined)) {
              arrHide = arrHide.concat(...[key, `${key}.subTotalAmount`, `${key}.taxAmount`, `${key}.totalAmount`]);
            }
            break;
          default:
        }
      } else if (this.entityName === RecordTypesFinance.SupplierInvoiceEntity || this.entityName === RecordTypesFinance.SupplierCreditNoteEntity || this.entityName === RecordTypesFinance.CreditNoteEntity) {
        if ((key === 'currencyCode' && (valueOld === null || valueOld === undefined) && (valueNew === null || valueNew === undefined)) || (key === 'currency' && (valueOld || !this.oldValues) && valueNew)) {
          arrHide.push('currencyCode');
        } else if ((key === 'currency' && (valueOld === null || valueOld === undefined) && (valueNew === null || valueNew === undefined))) {
          arrHide.push('currency');
        }
      }
    });
    return arrHide;
  };

  get displayAction() {
    if (this.action) {
      return AuditActions.find((e) => e?.id === this.action)?.name;
    }
    return ''
  };

}


export class RecordTypeModel extends BaseModel {
  @Expose()
  id!: string;

  @Expose()
  value!: string;

  @Expose()
  name!: string;
}
